import React, { FunctionComponent } from 'react';
import { useProperty } from '@spotify-internal/remote-config-integration-react';

import { usePillowTalk } from 'utils/remote-config/properties';
import { PillowTalkConfig, Vertical } from 'utils/vertical';
import { useServerSideConstants } from 'providers/ServerSideConstantsProvider';

import { LivePersonMessagingProvider } from './LivePersonMessagingProvider';
import { PillowTalkWrapper } from './PillowTalkMessagingProvider';
import { MessagingContext } from './MessagingContext';
import { DISABLED_STATUS } from './types';

interface Props {
  children: React.ReactNode;
}

const CONSUMER_PILLOW_TALK_CONFIG: PillowTalkConfig = {
  lineOfBusiness: 'CONSUMER',
  redirectUri: 'https://support.spotify.com/contact-spotify-anonymous/',
  showLoginDialog: true,
};

const MessagingProviderWrapper: FunctionComponent<Props> = ({ children }) => {
  const {
    verticalConfig: {
      verticalName,
      features,
      locales: { messagingLocales },
    },
    market: { marketCode, locale },
  } = useServerSideConstants();

  const usePillowTalkProperty = useProperty(usePillowTalk, { loadingValue: null });

  /*
    Edgecase: For consumer, we currently "temporarily" block Messaging alltogether for the ru-RU locale after 
    we left Russia, so the minor traffic from other RU markets (such as by-ru) only get email support
    (not even English Messaging fallback that we do for other locales). This is a formal requirement from CS.
  */
  if ((verticalName === Vertical.CONSUMER && locale === 'ru-RU') || features?.onlyEmailSupport) {
    return <MessagingContext.Provider value={{ status: DISABLED_STATUS }}>{children}</MessagingContext.Provider>;
  }

  if (features?.configurePillowTalk) {
    return (
      <PillowTalkWrapper
        locale={locale}
        configurePillowTalk={features.configurePillowTalk}
        vertical={verticalName}
        messagingLocales={messagingLocales}
      >
        {children}
      </PillowTalkWrapper>
    );
  }

  if (usePillowTalkProperty && verticalName === Vertical.CONSUMER) {
    return (
      <PillowTalkWrapper
        locale={locale}
        configurePillowTalk={CONSUMER_PILLOW_TALK_CONFIG}
        vertical={verticalName}
        messagingLocales={messagingLocales}
      >
        {children}
      </PillowTalkWrapper>
    );
  }

  return (
    <LivePersonMessagingProvider locale={locale} marketCode={marketCode}>
      {children}
    </LivePersonMessagingProvider>
  );
};

export default MessagingProviderWrapper;
